import { Fragment, useEffect, useId, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import templateMarkdown from "../../../../documentation/customer-template-release-notes.md?raw";
import extensionMarkdown from "../../../../documentation/extension-release-notes.md?raw";
import notesMarkdown from "../../../../documentation/portal-release-notes.md?raw";
import { useAuth } from "../auth/AuthProvider";
import { Card, PageHeader, vtag } from "../components/ui";
import { Inlines } from "../components/Inlines";
import { type Inline, type NoteVersion, parseReleaseNotes } from "../lib/release-notes";

const LINE_LABEL = { portal: "Portal", template: "Customer template", extension: "Agent extension" } as const;

/** CLAUDE.md section 9: the headings a release may carry, in the order they appear. */
const KINDS = ["Breaking", "Feature", "Enhancement", "Security", "Docs", "Internal", "Bug"] as const;

/**
 * The portal's own changelog, rendered from the file that is the record
 * (documentation/portal-release-notes.md, CLAUDE.md section 9) and bundled
 * at build time, so what a user reads here is exactly what shipped. The
 * footer links to `#v<version>` for the build they are on.
 *
 * `Unreleased` is what has merged but not yet been numbered: live in dev,
 * never in production. The internal team sees it; a customer never does.
 */
export function ReleaseNotesPage() {
  const { me } = useAuth();
  // Three deliverables, three lines (CLAUDE.md section 9): the portal a user is on, the template their stack runs, and the extension on their agents' machines.
  const all = useMemo(() => ({ versions: [...parseReleaseNotes(notesMarkdown).versions, ...parseReleaseNotes(templateMarkdown, "template").versions, ...parseReleaseNotes(extensionMarkdown, "extension").versions] }), []);
  const uiVersion = vtag(import.meta.env.VITE_PORTAL_VERSION ?? "dev");
  const { hash } = useLocation();
  const [term, setTerm] = useState("");
  const [kinds, setKinds] = useState<ReadonlySet<string>>(() => new Set(KINDS));
  const searchId = useId();

  const versions = useMemo(() => (me?.role === "internal-team" ? all.versions : all.versions.filter((v) => v.version !== "Unreleased")), [all, me?.role]);
  const needle = term.trim().toLowerCase();
  const narrowed = needle !== "" || kinds.size !== KINDS.length;
  const shown = useMemo(() => versions.map((v) => narrow(v, needle, kinds)).filter((v): v is NoteVersion => v !== undefined), [versions, needle, kinds]);
  const total = versions.reduce((n, v) => n + bulletCount(v), 0);
  const matched = shown.reduce((n, v) => n + bulletCount(v), 0);

  // On the hash only: re-running on filter state would steal focus from the filter box on every keystroke.
  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    target.scrollIntoView({ block: "start" });
    target.focus({ preventScroll: true });
  }, [hash]);

  const toggleKind = (k: string) =>
    setKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return (
    <>
      <PageHeader title="Release notes" subtitle={`What changed in each version of the portal (you are on ${uiVersion}), of the customer template your stack runs, and of the agent extension on your agents' machines.`} />
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_13rem] lg:items-start lg:gap-8">
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <label htmlFor={searchId} className="sr-only">
              Filter release notes
            </label>
            <input
              id={searchId}
              type="search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Filter"
              className="min-h-9 w-64 max-w-full rounded-lg border border-neutral-300 bg-white px-3 text-sm shadow-sm placeholder:text-neutral-400 focus:border-accent dark:border-neutral-700 dark:bg-neutral-900"
            />
            <div role="group" aria-label="Show only" className="flex flex-wrap gap-1.5">
              {KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={kinds.has(k)}
                  onClick={() => toggleKind(k)}
                  className={`min-h-8 rounded-full border px-3 text-xs font-medium transition-colors ${
                    kinds.has(k)
                      ? "border-accent bg-accent/10 text-accent-strong dark:text-neutral-100"
                      : "border-neutral-300 text-neutral-500 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
            {narrowed && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400" aria-live="polite">
                {matched} of {total}
              </span>
            )}
          </div>

          {shown.length === 0 && <p className="py-6 text-sm text-neutral-600 dark:text-neutral-400">Nothing matches{needle ? ` “${term.trim()}”` : ""}.</p>}
          <div className="space-y-6">
            {shown.map((v, i) => (
              <Fragment key={v.id}>
                {/* The deliverable being read, said once per group and again on every card: a reader deep in the list should never wonder. */}
                {(i === 0 || shown[i - 1]!.line !== v.line) && (
                  <h2 className={`text-sm font-semibold uppercase tracking-wider text-brand-text ${i === 0 ? "" : "mt-10 border-t border-neutral-200 pt-6 dark:border-neutral-800"}`}>
                    {LINE_LABEL[v.line]}
                  </h2>
                )}
              <Card>
                <section aria-labelledby={`${v.id}-heading`}>
                  <h3 id={v.id} tabIndex={-1} className="flex scroll-mt-6 flex-wrap items-baseline gap-x-3 text-xl font-semibold outline-none">
                    <span id={`${v.id}-heading`}>
                      {LINE_LABEL[v.line]} {v.version === "Unreleased" ? "Unreleased" : vtag(v.version)}
                    </span>
                    {v.date && <time dateTime={v.date} className="text-sm font-normal text-neutral-500 dark:text-neutral-400">{v.date}</time>}
                    {v.id === uiVersion && <span className="rounded-full border border-brand-text/40 px-2 py-0.5 text-xs font-medium text-brand-text">this build</span>}
                    {v.version === "Unreleased" && <span className="text-sm font-normal text-neutral-500 dark:text-neutral-400">merged, not yet numbered · internal team only</span>}
                  </h3>
                  {!narrowed && v.intro.map((p, i) => (
                    <p key={i} className="mt-2 text-sm text-neutral-600 dark:text-neutral-400"><Inlines parts={p} /></p>
                  ))}
                  {v.sections.map((s) => (
                    <div key={s.heading} className="mt-4">
                      {s.heading && <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{s.heading}</h4>}
                      <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm">
                        {s.bullets.map((b, i) => <li key={i}><Inlines parts={b} /></li>)}
                      </ul>
                    </div>
                  ))}
                </section>
              </Card>
              </Fragment>
            ))}
          </div>
        </div>

        {/* The versions are this page's headings, so they sit where every document's "On this page" sits: on the
            right. Captions carry the brand colour; entries read like any other rail, so the eye finds them. */}
        <nav aria-label="Versions" className="mt-6 lg:sticky lg:top-4 lg:mt-0 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">On this page</p>
          <ul className="mt-2 flex gap-1 overflow-x-auto lg:flex-col lg:gap-0.5">
            {shown.map((v, i) => {
              const current = hash === `#${v.id}`;
              const firstOfLine = i === 0 || shown[i - 1]!.line !== v.line;
              return (
                <li key={v.id} className="shrink-0">
                  {firstOfLine && (
                    <p className={`pb-1 text-xs font-semibold uppercase tracking-wider text-brand-text ${i === 0 ? "" : "ml-2 mt-5 border-t border-neutral-200 pt-3 lg:ml-0 dark:border-neutral-800"}`}>
                      {LINE_LABEL[v.line]}
                    </p>
                  )}
                  <Link
                    to={`#${v.id}`}
                    aria-current={current ? "location" : undefined}
                    className={`block whitespace-nowrap border-l-2 py-1 pl-2.5 pr-2 text-sm text-neutral-700 hover:underline dark:text-neutral-300 ${current ? "border-brand-text font-semibold" : "border-transparent"}`}
                  >
                    <span className="sr-only">{LINE_LABEL[v.line]} </span>
                    {v.version === "Unreleased" ? "Unreleased" : vtag(v.version)}
                    {v.id === uiVersion && <span className="ml-1.5 rounded-full border border-brand-text/40 px-1.5 text-[10px] font-medium text-brand-text">this build</span>}
                    {v.date && <span className="block text-xs text-neutral-500 dark:text-neutral-400">{v.date}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
}

/** A version narrowed to the bullets that match; nothing left means the version is not shown. */
function narrow(v: NoteVersion, needle: string, kinds: ReadonlySet<string>): NoteVersion | undefined {
  const sections = v.sections
    .filter((s) => s.heading === "" || kinds.has(s.heading))
    .map((s) => ({ ...s, bullets: needle ? s.bullets.filter((b) => plain(b).toLowerCase().includes(needle)) : s.bullets }))
    .filter((s) => s.bullets.length > 0);
  if (sections.length === 0 && (needle || kinds.size !== KINDS.length)) return undefined;
  return { ...v, sections };
}

const bulletCount = (v: NoteVersion) => v.sections.reduce((n, s) => n + s.bullets.length, 0);
const plain = (parts: Inline[]) => parts.map((p) => p.text).join("");

